import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Paper, IconButton, ListItemText, Menu, MenuItem, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useActiveInvestigation } from '../ResearchAgentTray/hooks/useActiveInvestigation'
import { usePortfolioData } from './PortfolioDataProvider'
import { useUtilityPanel, LAYOUT_PRESETS } from './UtilityPanelProvider'
import UtilityPanelToolbar from './UtilityPanelToolbar'
import { TOOL_REGISTRY } from './tools/toolRegistry'
import { useUtilityPanelStyles } from './utilityPanelStyles'
import ChartStack from './ChartStack'

const MIN_PANEL_FRACTION = 0.15
const MAX_PANEL_FRACTION = 0.85

// Header rendered when the panel is in chart-stack mode (not the
// exclusive Research overlay). The subject is shown as a button-styled
// dropdown -- the user can pick Household, "All <Grouping>", or keep
// the currently selected row (also surfaced when set). Mirrors the
// row select handles and breadcrumb segment handles, but is the
// most discoverable affordance for non-row scopes.
function ChartPanelHeader ({ onClose }) {
  const classes = useUtilityPanelStyles()
  const {
    selectedSubject,
    setSubjectLevel,
    clearSelectedSubject,
    groupings
  } = usePortfolioData()
  const [anchorEl, setAnchorEl] = useState(null)
  const openMenu = useCallback((e) => setAnchorEl(e.currentTarget), [])
  const closeMenu = useCallback(() => setAnchorEl(null), [])

  const subjectName = (() => {
    if (selectedSubject?.kind === 'row') return selectedSubject.row?.name || 'Selected row'
    if (selectedSubject?.kind === 'level') {
      return selectedSubject.label ? `All ${selectedSubject.label}` : 'All (level)'
    }
    return 'Household'
  })()

  const handlePickHousehold = useCallback(() => {
    clearSelectedSubject()
    closeMenu()
  }, [clearSelectedSubject, closeMenu])

  const handlePickLevel = useCallback((depth, label) => {
    setSubjectLevel(depth, label)
    closeMenu()
  }, [setSubjectLevel, closeMenu])

  const isHousehold = !selectedSubject

  return (
    <div className={classes.header}>
      <div className={classes.headerText}>
        <Typography component='div' className={classes.eyebrow}>Subject</Typography>
        <button
          type='button'
          className={classes.subjectButton}
          onClick={openMenu}
          aria-haspopup='menu'
          aria-expanded={Boolean(anchorEl)}
          title='Change chart subject'
        >
          <span className={classes.subjectName} title={subjectName}>{subjectName}</span>
          <span className={classes.subjectChevron} aria-hidden='true'>▾</span>
        </button>
      </div>
      <IconButton size='small' onClick={onClose} aria-label='Close panel'>
        <CloseIcon fontSize='small' />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          selected={isHousehold}
          onClick={handlePickHousehold}
        >
          <ListItemText
            primary='Household'
            secondary='All assets, no level pivot'
          />
        </MenuItem>
        {(groupings || []).map((label, depth) => {
          const isActive = selectedSubject?.kind === 'level' && selectedSubject.depth === depth
          return (
            <MenuItem
              key={`level_${depth}`}
              selected={isActive}
              onClick={() => handlePickLevel(depth, label)}
            >
              <ListItemText
                primary={`All ${label}`}
                secondary={`Aggregate by the ${label.toLowerCase()} grouping`}
              />
            </MenuItem>
          )
        })}
        {selectedSubject?.kind === 'row' && (
          <MenuItem selected>
            <ListItemText
              primary={selectedSubject.row?.name || 'Selected row'}
              secondary='Selected from the grid'
            />
          </MenuItem>
        )}
      </Menu>
    </div>
  )
}

export default function UtilityPanel () {
  const classes = useUtilityPanelStyles()
  const {
    isOpen,
    exclusiveTool,
    closeExclusive,
    closePanel,
    layoutMode,
    customRatio,
    setCustomRatio,
    effectivePreset,
    headerMeta,
    actions
  } = useUtilityPanel()

  // Drives the left-border pulse while a research investigation is in
  // flight. Wired here (not inside ResearchTool) so the cue is visible
  // even when another tool is foreground or the panel is closed-but-
  // about-to-reopen. Other tools can later add their own busy signals
  // by extending this OR-chain.
  const { isBusy: researchBusy } = useActiveInvestigation()

  const [isDragging, setIsDragging] = useState(false)
  const startXRef = useRef(0)
  const containerWidthRef = useRef(0)
  const startPanelFractionRef = useRef(0)
  const panelWrapperRef = useRef(null)

  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault()
    // Use the panel wrapper's offsetParent (the workArea flex-row) as
    // the reference width. This is the container whose total flex
    // budget the wrapper and the grid share.
    const container = panelWrapperRef.current?.parentElement
    if (!container) return
    containerWidthRef.current = container.offsetWidth
    startXRef.current = e.clientX
    const preset = customRatio || LAYOUT_PRESETS[layoutMode] || LAYOUT_PRESETS.sidebar
    startPanelFractionRef.current = preset.panelFlex / (preset.contentFlex + preset.panelFlex)
    setIsDragging(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [customRatio, layoutMode])

  const handleResizeDoubleClick = useCallback(() => {
    setCustomRatio(null)
  }, [setCustomRatio])

  useEffect(() => {
    if (!isDragging) return undefined

    const handleMouseMove = (e) => {
      const delta = startXRef.current - e.clientX
      const deltaFraction = delta / containerWidthRef.current
      const panelFraction = Math.min(
        MAX_PANEL_FRACTION,
        Math.max(MIN_PANEL_FRACTION, startPanelFractionRef.current + deltaFraction)
      )
      const contentFraction = 1 - panelFraction
      setCustomRatio({
        contentFlex: Math.round(contentFraction * 100),
        panelFlex: Math.round(panelFraction * 100)
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      // Restore body styles in case the panel unmounts mid-drag --
      // mouseup wouldn't fire to do it, and the page would otherwise
      // be stuck in `col-resize` cursor + non-selectable mode.
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, setCustomRatio])

  if (!isOpen) return null

  const renderingExclusive = !!exclusiveTool
  const exclusiveToolEntry = renderingExclusive ? TOOL_REGISTRY[exclusiveTool.toolId] : null
  if (renderingExclusive && !exclusiveToolEntry) return null

  const ExclusiveToolComponent = exclusiveToolEntry?.component || null
  const exclusivePayload = exclusiveTool?.payload || null
  const eyebrow = headerMeta?.eyebrow ?? exclusiveToolEntry?.label
  const title = headerMeta?.title ?? null
  const subjectLine = headerMeta?.subjectLine ?? null

  const handleClose = renderingExclusive ? closeExclusive : closePanel

  return (
    <div
      ref={panelWrapperRef}
      className={classes.panelWrapper}
      style={{
        flex: effectivePreset.panelFlex,
        transition: customRatio ? 'none' : undefined
      }}
    >
      <div
        className={`${classes.resizeHandle} ${isDragging ? classes.resizeHandleActive : ''}`}
        onMouseDown={handleResizeMouseDown}
        onDoubleClick={handleResizeDoubleClick}
        aria-label='Resize panel'
        role='separator'
        aria-orientation='vertical'
      >
        <div className={classes.resizeIndicator} />
      </div>

      <Paper
        className={`${classes.panel} ${researchBusy ? classes.panelBusy : ''}`}
        elevation={0}
        square
      >
        {renderingExclusive
          ? (
            <>
              <div className={classes.header}>
                {exclusiveToolEntry?.renderIcon && (
                  <span
                    className={exclusiveToolEntry.iconBackground === 'gradient'
                      ? classes.headerIcon
                      : classes.headerIconNeutral}
                    aria-hidden='true'
                  >
                    {exclusiveToolEntry.renderIcon()}
                  </span>
                )}
                <div className={classes.headerText}>
                  <Typography component='div' className={classes.eyebrow}>{eyebrow}</Typography>
                  {title && <Typography component='div' className={classes.headerTitle}>{title}</Typography>}
                  {subjectLine && <Typography component='div' className={classes.headerSubject}>{subjectLine}</Typography>}
                </div>
                <IconButton size='small' onClick={handleClose} aria-label='Close panel'>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </div>
              <UtilityPanelToolbar actions={actions} />
              <div className={classes.body}>
                {ExclusiveToolComponent && <ExclusiveToolComponent payload={exclusivePayload} />}
              </div>
            </>
          )
          : (
            <>
              <ChartPanelHeader onClose={handleClose} />
              <UtilityPanelToolbar />
              <ChartStack />
            </>
          )}
      </Paper>
    </div>
  )
}

ChartPanelHeader.propTypes = {
  onClose: PropTypes.func.isRequired
}
